import { create } from "zustand";
import {
	ApiStatusError,
	fetchCloudSharedVolumeChildren,
	fetchCloudSharedVolumes,
	type CloudSharedVolume,
} from "@/lib/api";
import {
	cloudSharedVolumeRowKey,
	type CloudSharedVolumeDirectory,
	type CloudSharedVolumeSelection,
} from "@/lib/cloud-shared-volumes";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";

/** ファイルのペインのツリー (~ か、クラウドの共有ボリューム)。 */
export type FileTreeKind = "home" | "cloud";

interface CloudSharedVolumeStoreState {
	/** 今見ている環境。 */
	environmentId: string | null;
	/** 環境を切り替えるたびに増やし、古い応答を捨てる。 */
	generation: number;
	/** 付いているボリューム。null は未取得か、停止中で分からない。 */
	volumes: CloudSharedVolume[] | null;
	/** 一覧の取得に失敗した (volumes が null でも取り直す)。 */
	volumesFailed: boolean;
	/** 読み込んだディレクトリ (キーは cloudSharedVolumeRowKey)。 */
	directories: ReadonlyMap<string, CloudSharedVolumeDirectory>;
	loadingDirectories: ReadonlySet<string>;
	/** ツリーで選んでいる行。~ の行を選んでいるとき・何も選んでいないときは null。 */
	selection: CloudSharedVolumeSelection | null;
	setSelection: (selection: CloudSharedVolumeSelection | null) => void;
	/**
	 * 最後に行を選んだツリー。選ぶたびに (同じ値でも) 差し替え、もう一方のツリーはこれを見て自分の選択を外す。
	 * 新規作成・アップロードの先が、見えている選択と食い違わないようにする。
	 */
	selectedTree: { tree: FileTreeKind };
	/** ~ を選んだら、共有ボリュームの選択 (selection) もすぐに外す。 */
	selectTree: (tree: FileTreeKind) => void;
	/** 消えた・移したパスとその下の、読み込んだディレクトリを捨てる。 */
	forgetDirectories: (volumeId: string, paths: readonly string[]) => void;
	/** 環境を切り替える。同じ環境なら何もしない。 */
	track: (environmentId: string | null) => void;
	/**
	 * ボリュームの一覧と、読み込んだディレクトリ (前に読んだ件数まで) を取り直す。停止中なら一覧を未取得に戻し、デバイスの
	 * 環境なら空にする。消えたディレクトリは外す。それ以外の失敗は、取れた分を載せてから投げる (複数なら AggregateError)。
	 */
	refresh: () => Promise<void>;
	/** ディレクトリの直下の最初の頁を読む。同じディレクトリを読んでいる途中なら、待たずに何もしない。 */
	loadDirectory: (volumeId: string, path: string) => Promise<void>;
	/** 続きの頁を読み足す。読む間に取り直しで一覧が差し替わったら、読んだ頁は捨てる。 */
	loadMore: (volumeId: string, path: string) => Promise<void>;
	/** 読み込んだディレクトリを前に読んだ件数まで読み直す (ファイルを置いた後)。読んでいなければ何もしない。 */
	reloadDirectory: (volumeId: string, path: string) => Promise<void>;
}

const EMPTY_DIRECTORIES: ReadonlyMap<string, CloudSharedVolumeDirectory> = new Map();
const RELOAD_CONCURRENCY = 4;

/** 前に読んだ件数に届くまで頁を読む (続きを開いていたディレクトリが縮まないように)。 */
async function fetchDirectoryPages(
	environmentId: string,
	volumeId: string,
	path: string,
	minimumEntries: number,
): Promise<CloudSharedVolumeDirectory> {
	const entries: CloudSharedVolumeDirectory["entries"] = [];
	let cursor: string | null = null;
	do {
		const page = await fetchCloudSharedVolumeChildren(environmentId, volumeId, path, cursor);
		entries.push(...page.entries);
		cursor = page.nextCursor;
	} while (cursor !== null && entries.length < minimumEntries);
	return { entries, nextCursor: cursor };
}

function isStatus(err: unknown, status: number): boolean {
	return err instanceof ApiStatusError && err.status === status;
}

function volumeIdOfKey(key: string): string {
	return key.split("/", 1)[0];
}

function pathOfKey(key: string): string {
	const volumeId = volumeIdOfKey(key);
	return key === volumeId ? "" : key.slice(volumeId.length + 1);
}

function withoutKey(set: ReadonlySet<string>, key: string): ReadonlySet<string> {
	const next = new Set(set);
	next.delete(key);
	return next;
}

/**
 * ファイルのツリーの共有ボリュームのデータ (クラウドの環境に mount された共有ボリューム)。中身はその場で Pod から読むので、
 * デバイスの写しのような数え直し・ポーリングは持たない。
 */
export const useCloudSharedVolumeStore = create<CloudSharedVolumeStoreState>((set, get) => {
	const current = (generation: number) => get().generation === generation;
	/** ディレクトリの読み込み・取り直しの通し番号と、キーごとに載っている結果の番号。後に始めた読み込みの結果だけを載せる。 */
	let directoryLoads = 0;
	let directoryLoadOf = new Map<string, number>();

	const reloadDirectories = async (
		environmentId: string,
		generation: number,
		volumes: readonly CloudSharedVolume[],
	): Promise<unknown[]> => {
		const load = ++directoryLoads;
		const mounted = new Set(volumes.filter((v) => v.mounted).map((v) => v.volumeId));
		const targets = [...get().directories].filter(([key]) => mounted.has(volumeIdOfKey(key)));
		const reloaded = new Map<string, CloudSharedVolumeDirectory | null>();
		const failures: unknown[] = [];
		let cursor = 0;
		await Promise.all(
			Array.from({ length: Math.min(RELOAD_CONCURRENCY, targets.length) }, async () => {
				while (cursor < targets.length) {
					const [key, previous] = targets[cursor++];
					try {
						reloaded.set(
							key,
							await fetchDirectoryPages(
								environmentId,
								volumeIdOfKey(key),
								pathOfKey(key),
								previous.entries.length,
							),
						);
					} catch (err) {
						// 消えたフォルダは木から外す。
						if (isStatus(err, 404)) reloaded.set(key, null);
						else failures.push(err);
					}
				}
			}),
		);
		if (!current(generation)) return [];
		const next = new Map<string, CloudSharedVolumeDirectory>();
		for (const [key, directory] of get().directories) {
			if (!mounted.has(volumeIdOfKey(key))) continue;
			const fresh = reloaded.get(key);
			if (fresh === undefined || load < (directoryLoadOf.get(key) ?? 0)) {
				next.set(key, directory);
				continue;
			}
			directoryLoadOf.set(key, load);
			if (fresh !== null) next.set(key, fresh);
		}
		set({ directories: next });
		return failures;
	};

	return {
		environmentId: null,
		generation: 0,
		volumes: null,
		volumesFailed: false,
		directories: EMPTY_DIRECTORIES,
		loadingDirectories: new Set(),
		selection: null,
		setSelection: (selection) => set({ selection }),
		selectedTree: { tree: "home" },
		selectTree: (tree) =>
			set(
				tree === "home" ? { selectedTree: { tree }, selection: null } : { selectedTree: { tree } },
			),
		forgetDirectories: (volumeId, paths) => {
			const keys = paths.map((path) => cloudSharedVolumeRowKey(volumeId, path));
			const forgotten = (key: string) =>
				keys.some((prefix) => key === prefix || key.startsWith(`${prefix}/`));
			const { directories } = get();
			if (![...directories.keys()].some(forgotten)) return;
			// 番号を進め、捨てる前に始まった読み込みに書き戻させない。
			for (const key of directories.keys())
				if (forgotten(key)) directoryLoadOf.set(key, ++directoryLoads);
			set({ directories: new Map([...directories].filter(([key]) => !forgotten(key))) });
		},
		track: (environmentId) => {
			if (get().environmentId === environmentId) return;
			directoryLoadOf = new Map();
			set((state) => ({
				environmentId,
				generation: state.generation + 1,
				volumes: null,
				volumesFailed: false,
				directories: EMPTY_DIRECTORIES,
				loadingDirectories: new Set(),
				selection: null,
			}));
		},
		refresh: async () => {
			const { environmentId, generation } = get();
			if (environmentId === null) return;
			let volumes: CloudSharedVolume[];
			try {
				volumes = await fetchCloudSharedVolumes(environmentId);
			} catch (err) {
				if (!current(generation)) return;
				// 停止中 (409) は起動してから取り直す。デバイスの環境 (400、下書きでまだ分からないとき) には出さない。
				if (isStatus(err, 409) || isStatus(err, 400)) {
					directoryLoadOf = new Map();
					set({
						volumes: isStatus(err, 409) ? null : [],
						volumesFailed: false,
						directories: EMPTY_DIRECTORIES,
					});
					return;
				}
				set({ volumesFailed: true });
				throw err;
			}
			if (!current(generation)) return;
			set({ volumes, volumesFailed: false });
			const failures = await reloadDirectories(environmentId, generation, volumes);
			if (failures.length === 1) throw failures[0];
			if (failures.length > 1)
				throw new AggregateError(failures, "共有ボリュームの取り直しに失敗しました。");
		},
		loadDirectory: async (volumeId, path) => {
			const { environmentId, generation, loadingDirectories } = get();
			const key = cloudSharedVolumeRowKey(volumeId, path);
			if (environmentId === null || loadingDirectories.has(key)) return;
			const load = ++directoryLoads;
			set({ loadingDirectories: new Set(loadingDirectories).add(key) });
			try {
				const directory = await fetchDirectoryPages(environmentId, volumeId, path, 0);
				if (!current(generation) || load < (directoryLoadOf.get(key) ?? 0)) return;
				directoryLoadOf.set(key, load);
				set((state) => ({ directories: new Map(state.directories).set(key, directory) }));
			} finally {
				if (current(generation)) {
					set((state) => ({ loadingDirectories: withoutKey(state.loadingDirectories, key) }));
				}
			}
		},
		loadMore: async (volumeId, path) => {
			const { environmentId, generation, loadingDirectories, directories } = get();
			const key = cloudSharedVolumeRowKey(volumeId, path);
			const loaded = directories.get(key);
			if (environmentId === null || !loaded?.nextCursor || loadingDirectories.has(key)) return;
			set({ loadingDirectories: new Set(loadingDirectories).add(key) });
			try {
				const page = await fetchCloudSharedVolumeChildren(
					environmentId,
					volumeId,
					path,
					loaded.nextCursor,
				);
				if (!current(generation)) return;
				set((state) =>
					state.directories.get(key) === loaded
						? {
								directories: new Map(state.directories).set(key, {
									entries: [...loaded.entries, ...page.entries],
									nextCursor: page.nextCursor,
								}),
							}
						: {},
				);
			} finally {
				if (current(generation)) {
					set((state) => ({ loadingDirectories: withoutKey(state.loadingDirectories, key) }));
				}
			}
		},
		reloadDirectory: async (volumeId, path) => {
			const { environmentId, generation, directories } = get();
			const key = cloudSharedVolumeRowKey(volumeId, path);
			const previous = directories.get(key);
			if (environmentId === null || previous === undefined) return;
			const load = ++directoryLoads;
			const directory = await fetchDirectoryPages(
				environmentId,
				volumeId,
				path,
				previous.entries.length,
			);
			if (!current(generation) || load < (directoryLoadOf.get(key) ?? 0)) return;
			directoryLoadOf.set(key, load);
			set((state) => ({ directories: new Map(state.directories).set(key, directory) }));
		},
	};
});

/**
 * ツリーに出している環境か、一覧の取得に失敗した環境なら取り直す (ターンの終わり・付け外しの後・ファイルのツリーの
 * 再読み込み)。
 */
export function refreshCloudSharedVolumes(environmentId: string): void {
	const store = useCloudSharedVolumeStore.getState();
	if (store.environmentId !== environmentId) return;
	if (store.volumes === null && !store.volumesFailed) return;
	reportSharedVolumeFailure(store.refresh(), "共有ボリュームを取り直せませんでした。");
}
